import React from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import {
  getVentasDocumentTypeLabel,
  getVentasPaymentMethodLabel,
} from '../../../utils/ventasSalesConstants';

const PAYMENT_COLORS = {
  efectivo: '#0d9488',
  transferencia: '#2563eb',
  yape: '#7c3aed',
  plin: '#0891b2',
  otro: '#64748b',
};

const formatMoney = (amount) =>
  `S/ ${parseFloat(amount || 0).toLocaleString('es-PE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatQty = (value) =>
  parseFloat(value || 0).toLocaleString('es-PE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

function KpiCard({ label, value, sub, accent = 'border-teal-500' }) {
  return (
    <div className={`bg-white rounded-xl shadow-sm p-5 border-l-4 ${accent}`}>
      <p className="text-sm text-gray-500 mb-1">{label}</p>
      <p className="text-2xl font-bold text-gray-900 truncate">{value}</p>
      {sub ? <p className="text-xs text-gray-500 mt-1">{sub}</p> : null}
    </div>
  );
}

function ChartCard({ title, subtitle, children, empty }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm h-full">
      <h3 className="text-base font-semibold text-gray-800">{title}</h3>
      {subtitle ? <p className="text-xs text-gray-500 mt-1 mb-4">{subtitle}</p> : <div className="mb-4" />}
      {empty ? (
        <p className="text-sm text-gray-500 py-16 text-center">Sin datos en el período.</p>
      ) : (
        children
      )}
    </div>
  );
}

function ProductTable({ rows, valueKey = 'revenue' }) {
  if (!rows?.length) {
    return <p className="text-sm text-gray-500 py-8 text-center">Sin datos en el período.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase text-gray-500 border-b">
            <th className="py-2 pr-3">#</th>
            <th className="py-2 pr-3">Producto</th>
            <th className="py-2 pr-3 text-right">Cant.</th>
            <th className="py-2 text-right">Ventas</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={row.product_id || idx} className="border-b border-gray-100 hover:bg-gray-50">
              <td className="py-2 pr-3 text-gray-400">{idx + 1}</td>
              <td className="py-2 pr-3">
                <div className="font-medium text-gray-800">{row.name}</div>
                {row.code ? <div className="text-xs font-mono text-gray-500">{row.code}</div> : null}
              </td>
              <td className="py-2 pr-3 text-right text-gray-600">{formatQty(row.quantity)}</td>
              <td className="py-2 text-right font-semibold text-teal-700">
                {valueKey === 'quantity' ? formatQty(row.quantity) : formatMoney(row.revenue)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function VentasSalesDashboardView({ data, filteredByPos = false }) {
  if (!data) return null;

  const { kpis = {}, daily_series: daily = [], by_payment_method: byMethod = [] } = data;
  const paymentChart = (byMethod || [])
    .filter((row) => Number(row.total_amount) > 0)
    .map((row) => ({
      ...row,
      name: getVentasPaymentMethodLabel(row.payment_method),
      value: Number(row.total_amount || 0),
    }));

  const dailyChart = (daily || []).map((row) => ({
    ...row,
    name: row.label || row.date,
    ventas: Number(row.total_amount || 0),
    tickets: Number(row.orders_count || 0),
  }));

  const topRevenueChart = (data.top_products_by_revenue || []).slice(0, 8).map((row) => ({
    name: (row.name || '').slice(0, 18),
    ventas: Number(row.revenue || 0),
  }));

  const posChart = (data.by_point_of_sale || []).slice(0, 8).map((row) => ({
    name: row.code || (row.name || '').slice(0, 12),
    ventas: Number(row.total_amount || 0),
    tickets: Number(row.orders_count || 0),
  }));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiCard
          label="Ventas totales"
          value={formatMoney(kpis.sales_total)}
          sub={`${kpis.orders_count || 0} ventas · Ticket ${formatMoney(kpis.avg_ticket)}`}
          accent="border-teal-500"
        />
        <KpiCard
          label="Efectivo"
          value={formatMoney(kpis.cash_total)}
          sub="Medio de pago efectivo"
          accent="border-emerald-500"
        />
        <KpiCard
          label="Digitales"
          value={formatMoney(kpis.digital_total)}
          sub="Transferencia + Yape + Plin"
          accent="border-indigo-500"
        />
        <KpiCard
          label="Otros medios"
          value={formatMoney(kpis.other_total)}
          sub={`${kpis.products_sold || 0} productos distintos`}
          accent="border-slate-500"
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <ChartCard title="Ventas por día" subtitle="Monto total diario del período" empty={!dailyChart.length}>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={dailyChart}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(value, name) => (name === 'ventas' ? formatMoney(value) : value)} />
                <Legend />
                <Line type="monotone" dataKey="ventas" name="Ventas" stroke="#0d9488" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="tickets" name="Tickets" stroke="#6366f1" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        <ChartCard title="Medios de pago" subtitle="Distribución del monto cobrado" empty={!paymentChart.length}>
          <div className="h-72 flex items-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={paymentChart} dataKey="value" nameKey="name" innerRadius={55} outerRadius={95} paddingAngle={2}>
                  {paymentChart.map((row) => (
                    <Cell key={row.payment_method} fill={PAYMENT_COLORS[row.payment_method] || '#94a3b8'} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatMoney(value)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <ChartCard title="Top productos por ventas" empty={!topRevenueChart.length}>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={topRevenueChart} layout="vertical" margin={{ left: 24 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(value) => formatMoney(value)} />
                <Bar dataKey="ventas" name="Ventas" fill="#0d9488" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        {!filteredByPos ? (
          <ChartCard title="Ventas por punto de venta" empty={!posChart.length}>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={posChart}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(value, name) => (name === 'ventas' ? formatMoney(value) : value)} />
                  <Legend />
                  <Bar dataKey="ventas" name="Ventas" fill="#2563eb" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>
        ) : (
          <ChartCard title="Detalle por medio de pago" empty={!paymentChart.length}>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase text-gray-500 border-b">
                    <th className="py-2 pr-3">Medio</th>
                    <th className="py-2 pr-3 text-right">Ventas</th>
                    <th className="py-2 text-right">Monto</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentChart.map((row) => (
                    <tr key={row.payment_method} className="border-b border-gray-100">
                      <td className="py-2 pr-3 font-medium text-gray-800">{row.name}</td>
                      <td className="py-2 pr-3 text-right">{row.orders_count}</td>
                      <td className="py-2 text-right font-semibold text-teal-700">{formatMoney(row.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ChartCard>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
          <h3 className="text-base font-semibold text-gray-800 mb-4">Ranking por monto</h3>
          <ProductTable rows={data.top_products_by_revenue} />
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
          <h3 className="text-base font-semibold text-gray-800 mb-4">Ranking por cantidad</h3>
          <ProductTable rows={data.top_products_by_quantity} valueKey="quantity" />
        </div>
      </div>

      {(data.by_document_type || []).some((row) => row.orders_count > 0) ? (
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
          <h3 className="text-base font-semibold text-gray-800 mb-4">Por tipo de documento</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {(data.by_document_type || [])
              .filter((row) => row.orders_count > 0)
              .map((row) => (
                <div key={row.document_type} className="rounded-lg bg-gray-50 p-4">
                  <p className="text-sm text-gray-500">{getVentasDocumentTypeLabel(row.document_type)}</p>
                  <p className="text-lg font-semibold text-gray-900 mt-1">{formatMoney(row.total_amount)}</p>
                  <p className="text-xs text-gray-500 mt-1">{row.orders_count} ventas</p>
                </div>
              ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
